import { useMutation, useQuery } from "convex/react";
import type { EvaluationRecoveryContinuation } from "@reminder-tracker/backend-contracts";
import { useState } from "react";

import {
  executeEvaluationRecovery,
  getEvaluationRecoveryJob,
  previewEvaluationRecovery,
  cancelEvaluationRecovery,
  resumeEvaluationRecovery,
} from "../../backend/functions";
import {
  AdministrationAuditDetails,
  AdministrationPageHeader,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

function reference(prefix: "corr" | "cmd"): string {
  if (typeof globalThis.crypto.randomUUID !== "function")
    throw new Error("A secure reference generator is unavailable.");
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

export function ProtectedEvaluationRecovery(): React.JSX.Element {
  const [correlationId] = useState(() => reference("corr"));
  const [continuation, setContinuation] = useState<EvaluationRecoveryContinuation | null>(null);
  const [reviewAccepted, setReviewAccepted] = useState(false);
  const [jobReference, setJobReference] = useState<string | null>(null);
  const [jobCorrelationId] = useState(() => reference("corr"));
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preview = useQuery(previewEvaluationRecovery, { correlationId, continuation });
  const execute = useMutation(executeEvaluationRecovery);
  const cancel = useMutation(cancelEvaluationRecovery);
  const resume = useMutation(resumeEvaluationRecovery);
  const job = useQuery(
    getEvaluationRecoveryJob,
    jobReference === null ? "skip" : { correlationId: jobCorrelationId, jobReference },
  );
  const candidateCount =
    preview === undefined
      ? 0
      : Object.values(preview.counts as Record<string, number>).reduce(
          (sum, value) => sum + value,
          0,
        );
  return (
    <>
      <AdministrationPageHeader
        title="Evaluation recovery"
        purpose="Review and recover scheduled reminder processing without sending messages or exposing reminder content."
        status={{ label: "Protected action", tone: "monitor" }}
      />
      <AdministrationWorkSurface
        id="evaluation-recovery-title"
        title="Controlled evaluation"
        purpose="Select and finish one read-only preview before starting, cancelling or resuming durable recovery work."
        state={{
          label:
            jobReference !== null
              ? job === undefined
                ? "Starting recovery…"
                : `Recovery ${job.state.replaceAll("-", " ")}`
              : preview === undefined
                ? "Preparing preview…"
                : preview.complete
                  ? reviewAccepted
                    ? "Ready to start"
                    : "Preview ready"
                  : "Preview incomplete",
          tone:
            job !== undefined && ["failed", "interrupted"].includes(job.state)
              ? "action"
              : jobReference !== null || preview?.complete === false
                ? "monitor"
                : preview?.complete
                  ? "healthy"
                  : "neutral",
        }}
        consequence="A preview never sends messages or changes reminders. Starting recovery may create eligible occurrence records, so it requires a completed preview and deliberate confirmation."
      >
        {preview === undefined ? (
          <p aria-live="polite">Preparing a read-only preview…</p>
        ) : (
          <div className="decision-stage">
            <p>
              <strong>
                {preview.complete ? "Complete preview" : "Incomplete — continue review"}
              </strong>
              {` · ${candidateCount} occurrence units · ${preview.counts.newEligible} newly eligible · ${preview.counts.restricted} restricted`}
            </p>
            <p>
              Completion means the window was evaluated. It does not mean a reminder was delivered
              or read.
            </p>
            {!preview.complete ? (
              <button
                className="secondary-action"
                type="button"
                disabled={busy || preview.continuation === null}
                onClick={() => {
                  if (preview.continuation === null) return;
                  setBusy(true);
                  setContinuation(preview.continuation);
                  setStatus("Continuing the same read-only preview…");
                  setBusy(false);
                }}
              >
                Continue read-only preview
              </button>
            ) : !reviewAccepted ? (
              <button
                className="secondary-action"
                type="button"
                onClick={() => setReviewAccepted(true)}
              >
                Review controlled evaluation
              </button>
            ) : (
              <button
                className="primary-action"
                type="button"
                disabled={jobReference !== null}
                onClick={() => {
                  setBusy(true);
                  setStatus(null);
                  void execute({
                    correlationId: reference("corr"),
                    commandKey: reference("cmd"),
                    preview: {
                      policyVersion: preview.policyVersion,
                      windowStartedAtEpochMilliseconds: preview.windowStartedAtEpochMilliseconds,
                      evaluatedThroughEpochMilliseconds: preview.evaluatedThroughEpochMilliseconds,
                      checkpointRevision: preview.checkpointRevision,
                      digest: preview.digest,
                      counts: preview.counts,
                      pageOrdinal: preview.pageOrdinal,
                      complete: true,
                    },
                    reason: "synthetic-certification",
                  })
                    .then((result) => {
                      setJobReference(result.jobReference);
                      setStatus("Controlled evaluation reserved. Durable progress is shown below.");
                    })
                    .catch(() =>
                      setStatus(
                        "The controlled evaluation was not started. Refresh the evidence and try again.",
                      ),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                Start controlled evaluation
              </button>
            )}
            {jobReference === null ? null : (
              <div>
                <p role="status">
                  <strong>{job?.state ?? "Starting"}</strong>
                  {job === undefined
                    ? " · Waiting for durable job evidence."
                    : ` · ${job.examinedRenewalCount} renewals examined · ${job.materializedOccurrenceCount} occurrences created.`}
                </p>
                {job !== undefined && ["validating", "running"].includes(job.state) ? (
                  <button
                    className="caution-action"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void cancel({
                        correlationId: reference("corr"),
                        commandKey: reference("cmd"),
                        jobReference: job.jobReference,
                        expectedRevision: job.revision,
                        reason: "operator-request",
                      })
                        .then(() =>
                          setStatus(
                            "Cancellation requested. The current bounded page will finish safely.",
                          ),
                        )
                        .catch(() =>
                          setStatus(
                            "The cancellation request was not applied. Refresh the durable job state.",
                          ),
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    Request safe cancellation
                  </button>
                ) : null}
                {job !== undefined && ["interrupted", "failed"].includes(job.state) ? (
                  <button
                    className="primary-action"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void resume({
                        correlationId: reference("corr"),
                        commandKey: reference("cmd"),
                        jobReference: job.jobReference,
                        expectedRevision: job.revision,
                        reason: "verification-after-repair",
                      })
                        .then(() => setStatus("Resume accepted under a fresh authorization fence."))
                        .catch(() =>
                          setStatus(
                            "The job was not resumed. Verify authorization and refresh its state.",
                          ),
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    Review and resume safely
                  </button>
                ) : null}
              </div>
            )}
            {status === null ? null : <p role="status">{status}</p>}
            <AdministrationAuditDetails summary="Preview policy, checkpoint and bounded page evidence">
              <dl className="audit-register">
                <div>
                  <dt>Policy version</dt>
                  <dd>{preview.policyVersion}</dd>
                </div>
                <div>
                  <dt>Checkpoint revision</dt>
                  <dd>{preview.checkpointRevision}</dd>
                </div>
                <div>
                  <dt>Preview page</dt>
                  <dd>{preview.pageOrdinal}</dd>
                </div>
                <div>
                  <dt>Preview complete</dt>
                  <dd>{preview.complete ? "Yes" : "No"}</dd>
                </div>
              </dl>
            </AdministrationAuditDetails>
          </div>
        )}
      </AdministrationWorkSurface>
    </>
  );
}
